/**
 * Weather Condition Mapping Utilities
 *
 * Maps OpenWeatherMap weather condition IDs to CSS classes for dynamic styling
 * Based on OpenWeatherMap's official weather condition codes
 * @see https://openweathermap.org/weather-conditions
 */

/**
 * Direct mapping from OpenWeatherMap condition IDs to specific weather types
 * This provides the most precise control over styling and icons for each weather condition
 */
export const WEATHER_CONDITION_ID_MAP = {
  // Group 800: Clear (01d/01n)
  800: 'clear-sky', // clear sky

  // Group 80x: Clouds
  801: 'few-clouds', // few clouds: 11-25% (02d/02n)
  802: 'scattered-clouds', // scattered clouds: 25-50% (03d/03n)
  803: 'broken-clouds', // broken clouds: 51-84% (04d/04n)
  804: 'broken-clouds', // overcast clouds: 85-100% (04d/04n)

  // Group 3xx: Drizzle (09d/09n)
  300: 'shower-rain', // light intensity drizzle
  301: 'shower-rain', // drizzle
  302: 'shower-rain', // heavy intensity drizzle
  310: 'shower-rain', // light intensity drizzle rain
  311: 'shower-rain', // drizzle rain
  312: 'shower-rain', // heavy intensity drizzle rain
  313: 'shower-rain', // shower rain and drizzle
  314: 'shower-rain', // heavy shower rain and drizzle
  321: 'shower-rain', // shower drizzle

  // Group 5xx: Rain
  500: 'rain', // light rain (10d/10n)
  501: 'rain', // moderate rain (10d/10n)
  502: 'rain', // heavy intensity rain (10d/10n)
  503: 'rain', // very heavy rain (10d/10n)
  504: 'rain', // extreme rain (10d/10n)
  511: 'snow', // freezing rain (13d/13n) - uses snow icon
  520: 'shower-rain', // light intensity shower rain (09d/09n)
  521: 'shower-rain', // shower rain (09d/09n)
  522: 'shower-rain', // heavy intensity shower rain (09d/09n)
  531: 'shower-rain', // ragged shower rain (09d/09n)

  // Group 6xx: Snow (13d/13n)
  600: 'snow', // light snow
  601: 'snow', // snow
  602: 'snow', // heavy snow
  611: 'snow', // sleet
  612: 'snow', // light shower sleet
  613: 'snow', // shower sleet
  615: 'snow', // light rain and snow
  616: 'snow', // rain and snow
  620: 'snow', // light shower snow
  621: 'snow', // shower snow
  622: 'snow', // heavy shower snow

  // Group 2xx: Thunderstorm (11d/11n)
  200: 'thunder', // thunderstorm with light rain
  201: 'thunder', // thunderstorm with rain
  202: 'thunder', // thunderstorm with heavy rain
  210: 'thunder', // light thunderstorm
  211: 'thunder', // thunderstorm
  212: 'thunder', // heavy thunderstorm
  221: 'thunder', // ragged thunderstorm
  230: 'thunder', // thunderstorm with light drizzle
  231: 'thunder', // thunderstorm with drizzle
  232: 'thunder', // thunderstorm with heavy drizzle

  // Group 7xx: Atmosphere (50d/50n)
  701: 'mist', // mist
  711: 'mist', // smoke
  721: 'mist', // haze
  731: 'mist', // sand/dust whirls
  741: 'mist', // fog
  751: 'mist', // sand
  761: 'mist', // dust
  762: 'mist', // volcanic ash
  771: 'mist', // squalls
  781: 'mist', // tornado
};

/**
 * Weather type categories for broader styling (backgrounds, etc.)
 * Maps specific weather types to broader categories for gradient backgrounds
 */
export const WEATHER_TYPE_CATEGORIES = {
  'clear-sky': 'clear', // 01d - Clear blue gradient
  'few-clouds': 'clear', // 02d - Clear blue gradient
  'scattered-clouds': 'clouds', // 03d - Cloudy gradient
  'broken-clouds': 'clouds', // 04d - Cloudy gradient
  'shower-rain': 'rain', // 09d - Rain gradient
  rain: 'rain', // 10d - Rain gradient
  thunder: 'storm', // 11d - Storm gradient
  snow: 'snow', // 13d - Snow gradient
  mist: 'atmosphere', // 50d - Atmospheric gradient
};

/**
 * Weather condition display names for UI
 */
export const WEATHER_CONDITION_NAMES = {
  clear: 'Clear',
  clouds: 'Cloudy',
  rain: 'Rainy',
  snow: 'Snowy',
  storm: 'Stormy',
  atmosphere: 'Atmospheric',
};

/**
 * Get specific weather type from condition ID
 *
 * @param {number} conditionId - Weather condition ID from OpenWeatherMap API
 * @return {string} Specific weather type (e.g., 'few-clouds', 'scattered-clouds')
 */
export function getWeatherType(conditionId) {
  if (!conditionId || typeof conditionId !== 'number') {
    return 'clear-sky';
  }

  return WEATHER_CONDITION_ID_MAP[conditionId] || 'clear-sky';
}

/**
 * Get weather category from condition ID
 *
 * @param {number} conditionId - Weather condition ID from OpenWeatherMap API
 * @return {string} Weather category (e.g., 'clear', 'clouds', 'rain')
 */
export function getWeatherCategory(conditionId) {
  const weatherType = getWeatherType(conditionId);
  return WEATHER_TYPE_CATEGORIES[weatherType] || 'clear';
}

/**
 * Get CSS class name for a weather condition background
 *
 * @param {number} conditionId - Weather condition ID from OpenWeatherMap API
 * @return {string} CSS class name for the condition background
 */
export function getWeatherClassName(conditionId) {
  const category = getWeatherCategory(conditionId);
  return `wp-block-bigbite-block-weather--weather-${category}`;
}

/**
 * Get CSS class name for a weather condition icon
 *
 * @param {number} conditionId - Weather condition ID from OpenWeatherMap API
 * @return {string} CSS class name for the specific weather type icon
 */
export function getWeatherTypeClassName(conditionId) {
  const weatherType = getWeatherType(conditionId);
  return `wp-block-bigbite-block-weather--${weatherType}`;
}

/**
 * Apply weather classes to the block element
 * Removes existing weather classes and adds new ones based on condition ID
 *
 * @param {Element} element     The block element
 * @param {number}  conditionId The weather condition ID
 */
export function applyWeatherClass(element, conditionId) {
  if (!element || !conditionId) {
    return;
  }

  // Remove all weather classes
  element.className = element.className.replace(/wp-block-bigbite-block-weather--\S+/g, '');

  // Add new weather classes
  const backgroundClass = getWeatherClassName(conditionId);
  const iconClass = getWeatherTypeClassName(conditionId);
  element.classList.add(backgroundClass, iconClass);
}
